import {
  IconArrowsLeftRight,
  IconBell,
  IconBellOff,
  IconBolt,
  IconChevronRight,
  IconCurrencyDollar,
  IconEye,
  IconNews,
  IconSpeakerphone,
  IconStack2,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useScrollLock from "@/hooks/useScrollLock";
import useDeleteNotification from "../../hooks/notifications/useDeleteNotification";
import useNotificationsFeed from "../../hooks/notifications/useNotificationsFeed";
import usePutMarkAsReadNotification from "../../hooks/notifications/usePutNotification";
import styles from "./notification.module.css";
import {NOTIFICATION_DRAWER_OPEN_EVENT} from "./notificationEvents";

const TABS = [
  {key: "all", icon: IconStack2},
  {key: "news", icon: IconNews},
  {key: "breaking", icon: IconBolt},
  {key: "movements", icon: IconArrowsLeftRight},
];

const formatTimeAgo = (dateString, t) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t("notificationJustNow");
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateString).toLocaleDateString();
};

const NewsCard = ({item, onNavigate, onDismiss, t}) => {
  const handleClick = () => {
    if (item.link) onNavigate(item);
  };

  const handleDismiss = e => {
    e.stopPropagation();
    onDismiss(item.id);
  };

  return (
    <div
      className={styles.newsCard}
      role="article"
      aria-label={item.title}
    >
      <div className={styles.newsBanner}>
        {item.image ? (
          <img
            src={item.image}
            alt=""
            className={styles.newsBannerImg}
            loading="lazy"
          />
        ) : (
          <div className={styles.newsBannerPlaceholder}>
            <IconNews size={32} />
          </div>
        )}
      </div>
      <div className={styles.newsBody}>
        <span className={`${styles.cardTag} ${styles.tagNews}`}>{t("notificationTagNews")}</span>
        <p className={styles.newsTitle}>{item.title}</p>
        <span className={styles.newsDate}>
          {new Date(item.createdAt).toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"})}
          {" · "}
          {new Date(item.createdAt).toLocaleTimeString(undefined, {hour: "2-digit", minute: "2-digit"})}
        </span>

        <div className={styles.newsFooter}>
          <button
            type="button"
            className={styles.ctaBtn}
            onClick={handleClick}
          >
            {t("notificationNewsCta")}
            <IconChevronRight size={14} />
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
            onClick={handleDismiss}
            aria-label={t("notificationDrawerDeleteBtn")}
          >
            <IconTrash size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

const PushCard = ({item, onNavigate, onDelete, onMarkRead, t}) => {
  const handleClick = () => {
    if (item.link) onNavigate(item);
  };

  const handleDelete = e => {
    e.stopPropagation();
    if (item.originalId) onDelete(item.originalId);
  };

  const handleMarkRead = e => {
    e.stopPropagation();
    if (!item.isRead && item.originalId) onMarkRead(item.originalId);
  };

  const isMoney = item.subtype === "money";

  return (
    <div
      className={`${styles.card} ${!item.isRead ? styles.cardUnread : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && handleClick()}
      aria-label={item.title}
    >
      <div className={`${styles.cardIcon} ${isMoney ? styles.cardIconMoney : ""}`}>
        {isMoney ? <IconCurrencyDollar size={18} /> : <IconSpeakerphone size={18} />}
      </div>

      <div className={styles.cardContent}>
        <p className={styles.cardTitle}>{item.title}</p>
        {item.body && <p className={styles.cardBody}>{item.body}</p>}
        <div className={styles.cardMeta}>
          <span className={styles.cardTime}>{formatTimeAgo(item.createdAt, t)}</span>
          <span className={`${styles.cardTag} ${isMoney ? styles.tagMoney : styles.tagBreaking}`}>
            {isMoney ? t("notificationTagMovement") : t("notificationTagBreaking")}
          </span>
        </div>
      </div>

      <div className={styles.cardActions}>
        {!item.isRead && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={handleMarkRead}
            aria-label={t("notificationMarkRead")}
          >
            <IconEye size={14} />
          </button>
        )}
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
          onClick={handleDelete}
          aria-label={t("notificationDrawerDeleteBtn")}
        >
          <IconTrash size={14} />
        </button>
      </div>
    </div>
  );
};

const NotificationMenu = ({showBellTrigger = true}) => {
  const {feed, unreadCount, refetch} = useNotificationsFeed();
  const {markAsRead} = usePutMarkAsReadNotification();
  const {deleteNotification} = useDeleteNotification();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [dismissedNews, setDismissedNews] = useState([]);
  const navigate = useNavigate();
  const {t} = useTranslation();

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener(NOTIFICATION_DRAWER_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(NOTIFICATION_DRAWER_OPEN_EVENT, handleOpen);
  }, []);

  useScrollLock(open);

  const filteredFeed = useMemo(() => {
    if (activeTab === "all") return feed;
    if (activeTab === "news") return feed.filter(item => item.type === "news");
    if (activeTab === "breaking") return feed.filter(item => item.type === "push" && item.subtype !== "money");
    if (activeTab === "movements") return feed.filter(item => item.type === "push" && item.subtype === "money");
    return feed;
  }, [feed, activeTab]);

  const handleNavigate = useCallback(
    item => {
      if (!item.isRead && item.type === "push" && item.originalId) {
        markAsRead(item.originalId);
      }
      if (item.link) {
        navigate(item.link);
      }
      setOpen(false);
    },
    [navigate, markAsRead]
  );

  const handleDelete = useCallback(
    id => {
      deleteNotification(id);
    },
    [deleteNotification]
  );

  const handleMarkRead = useCallback(
    id => {
      markAsRead(id);
    },
    [markAsRead]
  );

  const handleDismissNews = useCallback(id => {
    setDismissedNews(prev => [...prev, id]);
  }, []);

  const filteredFeedWithDismissed = useMemo(
    () => filteredFeed.filter(item => !dismissedNews.includes(item.id)),
    [filteredFeed, dismissedNews]
  );

  const handleClose = () => {
    setOpen(false);
    refetch();
  };

  return (
    <>
      {showBellTrigger && (
        <NotificationBellTrigger
          unreadCount={unreadCount}
          onOpen={() => setOpen(true)}
        />
      )}

      {/* Overlay */}
      <div
        className={`${styles.overlay} ${open ? styles.overlayVisible : ""}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${open ? styles.sidebarOpen : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("notificationDrawerTitle")}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            {t("notificationDrawerTitle")}
            {unreadCount > 0 && <span className={styles.headerBadge}>{unreadCount}</span>}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label={t("notificationClose")}
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div
          className={styles.tabs}
          role="tablist"
        >
          {TABS.map(({key, icon: Icon}) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={14} />
              {t(`notificationTab_${key}`)}
            </button>
          ))}
        </div>

        {/* Feed */}
        <div className={styles.feedContainer}>
          {filteredFeedWithDismissed.length > 0 ? (
            filteredFeedWithDismissed.map(item =>
              item.type === "news" ? (
                <NewsCard
                  key={item.id}
                  item={item}
                  onNavigate={handleNavigate}
                  onDismiss={handleDismissNews}
                  t={t}
                />
              ) : (
                <PushCard
                  key={item.id}
                  item={item}
                  onNavigate={handleNavigate}
                  onDelete={handleDelete}
                  onMarkRead={handleMarkRead}
                  t={t}
                />
              )
            )
          ) : (
            <div className={styles.emptyState}>
              <IconBellOff
                size={48}
                className={styles.emptyIcon}
              />
              <p className={styles.emptyTitle}>{t("notificationDrawerNotifications")}</p>
              <p className={styles.emptySubtext}>{t("notificationEmptySubtext")}</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export const NotificationBellTrigger = ({unreadCount, onOpen}) => {
  const {t} = useTranslation();

  return (
    <div
      className={styles.bellWrapper}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onOpen()}
      aria-label={t("notificationDrawerTitle")}
    >
      <IconBell className={styles.bellIcon} />
      {unreadCount > 0 && <span className={styles.bellBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
    </div>
  );
};

NotificationBellTrigger.propTypes = {
  unreadCount: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
};

NotificationMenu.propTypes = {
  showBellTrigger: PropTypes.bool,
};

export default NotificationMenu;
